import React from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import EmailIcon from "@common/assets/images/share-shake/email-a-friend.png";
import CoffeeIcon from "@common/assets/images/share-shake/mention-over-coffee.png";
import SocialIcon from "@common/assets/images/share-shake/post-to-social.png";
import * as Icon from "@common/components/DetailsPane/icons";
import Heading from "@common/components/Heading/Heading";
import LargeButton from "@common/components/LargeButton/LargeButton";
import { LargeButtonGroup } from "@common/components/LargeButton/LargeButtonGroup.styles";
import PageWrap from "@common/components/PageWrap/PageWrap";
import displayToast from "@common/components/Toast/displayToast";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import NavigationCustomElement from "@main/layouts/components/PageNavigation/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import * as Styled from "@main/pages/shared/styles/DetailsWrapper.styles";
import { MODULE_LIST_PATHS, useCurrentPathRouteConfig } from "@main/router/util/useCurrentPathRouteConfig";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";

import * as PageStyled from "./ShareShakePage.styles";

export function ShareShakePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ appKey: string }>();
  const { selectedWorkspace } = useAppSelectionContext();
  const { moduleRoot } = useCurrentPathRouteConfig();
  const { sendEvent } = useAnalytics();

  const title = (location.state as { title?: string } | undefined)?.title ?? "Share Shake";

  const shareText =
    "Found this tool Shake — tells us if a build's safe to ship before we push it live. Worth checking out: http://www.shakebugs.com";

  const onShareToSocialClick = () => {
    sendEvent("share_shake_social_clicked");
    navigator.clipboard.writeText(shareText);
    displayToast({
      title: "Share text copied",
      content: "Paste it into your post on LinkedIn, X, Discord or wherever your team hangs out.",
      duration: 4000,
    });
  };

  const onEmailAFriendClick = () => {
    sendEvent("share_shake_email_clicked");

    const subject = encodeURIComponent("Thought you might find this useful");
    const body = encodeURIComponent(
      "Hey, check this out. Shake tells us if a build's safe to ship before we push it live. Pretty useful: www.shakebugs.com",
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const onMentionOverCoffeeClick = () => {
    sendEvent("share_shake_coffee_clicked");
    displayToast({
      title: "Honestly, that's our favorite way to grow. Thanks for being a legend!",
      content: "If they ask for a link, it's just www.shakebugs.com. Enjoy your coffee! ☕",
      duration: 6000,
    });
  };

  const onBackClick = () => {
    const listPath = moduleRoot ? MODULE_LIST_PATHS[moduleRoot] : undefined;
    if (!listPath) return;

    navigate(generatePath(listPath, { workspaceSlug: selectedWorkspace?.slug ?? null, appKey: params.appKey ?? null }));
  };

  const CustomElement = (
    <NavigationCustomElement>
      <div>
        <Tooltip
          text={"Back"}
          position={"bottom"}
        >
          <Styled.NavbarNavigation onClick={onBackClick}>
            <Icon.SendIcon
              height={20}
              width={20}
            />
          </Styled.NavbarNavigation>
        </Tooltip>
      </div>
      <Styled.DetailsTitle>
        <p>{title}</p>
      </Styled.DetailsTitle>
    </NavigationCustomElement>
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  return (
    <PageWrap style={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center", height: "100%" }}>
      <PageStyled.Container>
        <Heading
          style={{ maxWidth: "67rem", textAlign: "center" }}
          className={"center"}
          heading1
          marginBottom={28}
        >
          {"We grow through word of mouth — want to help?"}
        </Heading>

        <LargeButtonGroup>
          <LargeButton
            image={SocialIcon}
            title={"Post to social"}
            subtitle={`Whether it's LinkedIn, X, \nDiscord... click to share a link`}
            onClick={onShareToSocialClick}
          />
          <LargeButton
            image={EmailIcon}
            title={"Email a friend"}
            subtitle={`You have an exact person in \nmind to send to`}
            onClick={onEmailAFriendClick}
          />
          <LargeButton
            image={CoffeeIcon}
            title={"Mention over a coffee"}
            subtitle={`The warmest way to share \nsoftware recommendations`}
            onClick={onMentionOverCoffeeClick}
          />
        </LargeButtonGroup>
      </PageStyled.Container>
    </PageWrap>
  );
}
